import React, { useState, useCallback, useRef } from 'react';
import { C } from '../tokens';
import Icon from '../components/Icon';
import { Btn, Breadcrumb, Pill } from '../components/UI';
import useBreakpoint from '../useBreakpoint';

/* -------------------------------------------
   Constants
   ------------------------------------------- */
const DRAG_THRESHOLD = 5;
const PRIORITY_COLORS = { High: C.red, Medium: C.amber, Low: C.green };
const STATUS_COLORS = { active: '#16a34a', pending: C.amber, cold: C.t3 };

const ICON_OPTIONS = [
  { icon: 'person', bg: '#dbeafe', color: '#3b82f6' },
  { icon: 'business', bg: `${C.primary}18`, color: C.primary },
  { icon: 'rocket_launch', bg: '#f3e8ff', color: '#a855f7' },
  { icon: 'chat_bubble', bg: '#fef3c7', color: C.amber },
  { icon: 'calendar_month', bg: `${C.primary}18`, color: C.primary },
  { icon: 'verified', bg: '#d1fae5', color: C.teal },
  { icon: 'star', bg: '#fef3c7', color: C.amber },
];

const INITIAL_COLUMNS = [
  {
    id: 'new-lead', title: 'New Lead', color: '#3b82f6',
    cards: [
      { id: 'c1', name: 'Acme Innovations', tags: ['AI Integration'], icon: 'person', iconBg: '#dbeafe', iconColor: '#3b82f6', status: 'active', priority: 'High', value: '$24,000', contact: 'Sarah Chen', email: 'sarah@acme.io', notes: 'Interested in chatbot integration for customer support.', createdAt: '2 days ago' },
      { id: 'c2', name: 'Global Tech Corp', tags: ['Enterprise', 'API'], icon: 'business', iconBg: `${C.primary}18`, iconColor: C.primary, status: 'active', priority: 'Medium', value: '$48,000', contact: 'James Wright', email: 'james@globaltech.com', notes: 'Looking for enterprise API access with custom SLA.', createdAt: '5 days ago' },
      { id: 'c3', name: 'Nova Digital', tags: ['Startup'], icon: 'rocket_launch', iconBg: '#f3e8ff', iconColor: '#a855f7', status: 'pending', priority: 'Low', value: '$8,500', contact: 'Emily Davis', email: 'emily@novadigital.co', notes: 'Early-stage startup exploring AI tools.', createdAt: '1 week ago' },
    ],
  },
  {
    id: 'engaged', title: 'Engaged', color: '#16a34a',
    cards: [
      { id: 'c4', name: 'Starlight Agency', tags: ['Consulting'], icon: 'chat_bubble', iconBg: '#fef3c7', iconColor: C.amber, status: 'active', priority: 'High', value: '$36,000', contact: 'Michael Torres', email: 'mike@starlight.agency', notes: 'Next call scheduled for tomorrow. Very interested in analytics.', createdAt: '3 days ago', sub: 'Next call: Tomorrow' },
    ],
  },
  {
    id: 'info-sent', title: 'Info Sent', color: '#a855f7',
    cards: [],
  },
  {
    id: 'meeting-set', title: 'Meeting Set', color: C.primary,
    cards: [
      { id: 'c5', name: 'Vertex Systems', tags: ['Enterprise'], icon: 'calendar_month', iconBg: `${C.primary}18`, iconColor: C.primary, status: 'active', priority: 'High', value: '$72,000', contact: 'Liam Park', email: 'liam@vertex.systems', notes: 'Demo scheduled for Friday. Decision maker confirmed attendance.', createdAt: '1 week ago', sub: 'Demo scheduled Fri' },
    ],
  },
  {
    id: 'closed-won', title: 'Closed Won', color: C.teal,
    cards: [
      { id: 'c6', name: 'Pinnacle Health', tags: ['Healthcare', 'API'], icon: 'verified', iconBg: '#d1fae5', iconColor: C.teal, status: 'active', priority: 'High', value: '$95,000', contact: 'Anna Bell', email: 'anna@pinnacle.health', notes: 'Contract signed. Onboarding starts next Monday.', createdAt: '2 weeks ago' },
    ],
  },
];

/* -------------------------------------------
   Main Component
   ------------------------------------------- */
export default function PipelinePage() {
  const { isMobile, isTablet, isDesktop, isWide } = useBreakpoint();
  const mob = isMobile;
  const pad = mob ? 16 : isTablet ? 24 : 36;

  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [search, setSearch] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addToColumn, setAddToColumn] = useState(null);
  const [formData, setFormData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  /* Mobile forces list view; desktop/tablet can toggle */
  const [viewMode, setViewMode] = useState('board');
  const effectiveView = mob ? 'list' : viewMode;

  /* --- Drag state (desktop only) --- */
  const dragRef = useRef(null);
  const dropTargetRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const cardRectsRef = useRef({});
  const colRectsRef = useRef({});
  const boardRef = useRef(null);

  // Keep ref in sync with state
  const updateDropTarget = useCallback((val) => {
    dropTargetRef.current = val;
    setDropTarget(val);
  }, []);

  /* --- Filtered columns --- */
  const filteredColumns = columns.map(col => ({
    ...col,
    cards: col.cards.filter(c =>
      !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
    ),
  }));

  /* --- Pipeline stats --- */
  const totalLeads = columns.reduce((s, c) => s + c.cards.length, 0);
  const totalValue = columns.reduce((s, c) => s + c.cards.reduce((v, card) => {
    const num = parseInt(card.value?.replace(/[^0-9]/g, '') || '0');
    return v + num;
  }, 0), 0);

  /* -------------------------------------------
     DRAG ENGINE (desktop only � disabled on touch)
     ------------------------------------------- */

  const cacheRects = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const colEls = board.querySelectorAll('[data-col-id]');
    const cR = {};
    colEls.forEach(el => {
      const r = el.getBoundingClientRect();
      cR[el.dataset.colId] = { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
    });
    colRectsRef.current = cR;

    const cdR = {};
    colEls.forEach(el => {
      const colId = el.dataset.colId;
      const cards = el.querySelectorAll('[data-card-id]');
      cdR[colId] = Array.from(cards).map(c => {
        const r = c.getBoundingClientRect();
        return { id: c.dataset.cardId, top: r.top, bottom: r.bottom, midY: r.top + r.height / 2 };
      });
    });
    cardRectsRef.current = cdR;
  }, []);

  const findDropColumn = useCallback((clientX) => {
    for (const [colId, r] of Object.entries(colRectsRef.current)) {
      if (clientX >= r.left && clientX <= r.right) return colId;
    }
    let best = null, bestDist = Infinity;
    for (const [colId, r] of Object.entries(colRectsRef.current)) {
      const d = Math.abs(clientX - (r.left + r.right) / 2);
      if (d < bestDist) { bestDist = d; best = colId; }
    }
    return best;
  }, []);

  const findDropIndex = useCallback((colId, clientY, draggedId) => {
    const cards = (cardRectsRef.current[colId] || []).filter(c => c.id !== draggedId);
    if (cards.length === 0) return 0;
    for (let i = 0; i < cards.length; i++) {
      if (clientY < cards[i].midY) return i;
    }
    return cards.length;
  }, []);

  const handleCardMouseDown = useCallback((card, colId, e) => {
    /* Disable drag on mobile/tablet � tap opens detail instead */
    if (!isDesktop) return;
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();

    const cardEl = e.currentTarget;
    const rect = cardEl.getBoundingClientRect();

    dragRef.current = {
      cardId: card.id, card, sourceColId: colId,
      startX: e.clientX, startY: e.clientY,
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      width: rect.width, height: rect.height, activated: false,
    };

    const onMove = (me) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = me.clientX - drag.startX;
      const dy = me.clientY - drag.startY;
      if (!drag.activated) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        drag.activated = true;
        cacheRects();
        setDragState({ cardId: drag.cardId, sourceColId: drag.sourceColId, ghostLeft: me.clientX - drag.offsetX, ghostTop: me.clientY - drag.offsetY, ghostW: drag.width, ghostH: drag.height });
      }
      setDragState(prev => prev ? ({ ...prev, ghostLeft: me.clientX - drag.offsetX, ghostTop: me.clientY - drag.offsetY }) : null);
      const colId2 = findDropColumn(me.clientX);
      if (colId2) {
        const idx = findDropIndex(colId2, me.clientY, drag.cardId);
        updateDropTarget({ colId: colId2, index: idx });
      }
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag || !drag.activated) {
        setDragState(null); updateDropTarget(null);
        if (drag) setSelectedCard(drag.card);
        return;
      }

      // Read from ref (always fresh)
      const target = dropTargetRef.current;
      setDragState(null); updateDropTarget(null);
      if (!target) return;
      setColumns(prev => {
        // Remove card from all columns
        let movedCard = null;
        const cleaned = prev.map(c => ({ ...c, cards: c.cards.filter(cd => { if (cd.id === drag.cardId) { movedCard = cd; return false; } return true; }) }));
        if (!movedCard) return prev;

        // Insert at target
        return cleaned.map(col => {
          if (col.id !== target.colId) return col;
          const newCards = [...col.cards];
          newCards.splice(target.index, 0, movedCard);
          return { ...col, cards: newCards };
        });
      });
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isDesktop, cacheRects, findDropColumn, findDropIndex, updateDropTarget]);

  /* -------------------------------------------
     CRUD
     ------------------------------------------- */

  const openAddModal = (colId = null) => {
    setEditingId(null);
    setAddToColumn(colId || columns[0]?.id);
    setFormData({ name: '', tags: '', icon: 'person', iconBg: '#dbeafe', iconColor: '#3b82f6', status: 'active', priority: 'Medium', value: '', contact: '', email: '', notes: '' });
    setShowAddModal(true);
  };

  const openEditModal = (card, colId) => {
    const currentColId = colId || columns.find(col => col.cards.some(c => c.id === card.id))?.id || columns[0]?.id;
    setEditingId(card.id);
    setAddToColumn(currentColId);
    setFormData({ name: card.name, tags: card.tags.join(', '), icon: card.icon, iconBg: card.iconBg, iconColor: card.iconColor, status: card.status || 'active', priority: card.priority || 'Medium', value: card.value || '', contact: card.contact || '', email: card.email || '', notes: card.notes || '' });
    setSelectedCard(null);
    setShowAddModal(true);
  };

  const saveCard = () => {
    if (!formData?.name?.trim()) return;
    const card = {
      id: editingId || `c${Date.now()}`,
      name: formData.name.trim(),
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      icon: formData.icon, iconBg: formData.iconBg, iconColor: formData.iconColor,
      status: formData.status, priority: formData.priority,
      value: formData.value, contact: formData.contact, email: formData.email, notes: formData.notes,
      createdAt: editingId ? undefined : 'Just now',
    };
    setColumns(prev => {
      if (editingId) {
        // Find which column currently holds this card
        const sourceColId = prev.find(col => col.cards.some(c => c.id === editingId))?.id;
        if (sourceColId && addToColumn && sourceColId !== addToColumn) {
          // Move card to new column
          const movedCard = prev.flatMap(col => col.cards).find(c => c.id === editingId);
          const updated = { ...movedCard, ...card, createdAt: movedCard?.createdAt };
          return prev.map(col => {
            if (col.id === sourceColId) return { ...col, cards: col.cards.filter(c => c.id !== editingId) };
            if (col.id === addToColumn) return { ...col, cards: [...col.cards, updated] };
            return col;
          });
        }
        return prev.map(col => ({ ...col, cards: col.cards.map(c => c.id === editingId ? { ...c, ...card, createdAt: c.createdAt } : c) }));
      }
      return prev.map(col => col.id === addToColumn ? { ...col, cards: [...col.cards, card] } : col);
    });
    setShowAddModal(false); setFormData(null); setEditingId(null);
  };

  const deleteCard = (cardId) => {
    setColumns(prev => prev.map(col => ({ ...col, cards: col.cards.filter(c => c.id !== cardId) })));
    setSelectedCard(null);
  };

  /* --- Get dragged card data --- */
  const draggedCard = dragState ? (() => {
    for (const col of columns) { const c = col.cards.find(cd => cd.id === dragState.cardId); if (c) return c; }
    return null;
  })() : null;

  /* -------------------------------------------
     RENDER HELPERS
     ------------------------------------------- */

  const renderCard = (card, colId, isDragGhost = false) => {
    const isBeingDragged = dragState?.cardId === card.id && !isDragGhost;
    return (
      <div
        key={card.id}
        data-card-id={card.id}
        onMouseDown={isDragGhost ? undefined : (e) => handleCardMouseDown(card, colId, e)}
        onClick={!isDesktop && !isDragGhost ? () => setSelectedCard(card) : undefined}
        style={{
          background: C.w, borderRadius: 14,
          border: `1px solid ${isDragGhost ? 'transparent' : C.bdr}`,
          padding: mob ? 14 : 16,
          cursor: isDragGhost ? 'grabbing' : isDesktop ? 'grab' : 'pointer',
          userSelect: 'none',
          opacity: isBeingDragged ? 0.2 : 1,
          transition: isBeingDragged ? 'opacity .15s' : 'box-shadow .15s, transform .1s',
          boxShadow: isDragGhost ? '0 16px 48px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.08)' : '0 1px 3px rgba(0,0,0,.03)',
          transform: isDragGhost ? 'rotate(2deg) scale(1.02)' : 'none',
          position: 'relative',
        }}
        onMouseEnter={isDragGhost || !isDesktop ? undefined : (e) => {
          if (!dragState) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }
        }}
        onMouseLeave={isDragGhost || !isDesktop ? undefined : (e) => {
          if (!dragState) { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.03)'; e.currentTarget.style.transform = 'none'; }
        }}
      >
        {/* Top: icon + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: card.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon name={card.icon} size={18} style={{ color: card.iconColor }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {card.priority && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: `${PRIORITY_COLORS[card.priority]}14`, color: PRIORITY_COLORS[card.priority] }}>
                {card.priority}
              </span>
            )}
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[card.status] || C.t3, boxShadow: `0 0 0 2px ${STATUS_COLORS[card.status] || C.t3}30` }} />
          </div>
        </div>
        {/* Name */}
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{card.name}</div>
        {/* Value */}
        {card.value && <div style={{ fontSize: 12, fontWeight: 700, color: C.teal, marginBottom: 8 }}>{card.value}</div>}
        {/* Tags */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: (card.sub || card.contact) ? 10 : 0 }}>
          {card.tags.map(t => (
            <span key={t} style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#f1eeec', color: C.t2, textTransform: 'uppercase', letterSpacing: '.02em' }}>{t}</span>
          ))}
        </div>
        {/* Footer */}
        {(card.sub || card.contact) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: `1px solid ${C.bdr}`, fontSize: 10, color: C.t3 }}>
            <span>{card.sub || card.contact}</span>
            {card.contact && !card.sub && (
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${card.iconColor}18`, display: 'grid', placeItems: 'center' }}>
                <Icon name="person" size={13} style={{ color: card.iconColor }} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  /* --- Drop indicator --- */
  const DropLine = ({ color }) => (
    <div style={{ height: 3, background: color, borderRadius: 3, margin: '2px 0', position: 'relative' }}>
      <div style={{ position: 'absolute', left: -4, top: -3, width: 9, height: 9, borderRadius: '50%', background: color }} />
      <div style={{ position: 'absolute', right: -4, top: -3, width: 9, height: 9, borderRadius: '50%', background: color }} />
    </div>
  );

  /* --- Form helpers --- */
  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: C.t2, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.04em' };
  const focusHandlers = {
    onFocus: e => { e.target.style.borderColor = C.primary; e.target.style.boxShadow = `0 0 0 3px ${C.pL}`; },
    onBlur: e => { e.target.style.borderColor = C.bdr; e.target.style.boxShadow = 'none'; },
  };

  /* Modal widths */
  const detailW = mob ? '92%' : 440;
  const formW = mob ? '94%' : 480;

  /* -------------------------------------------
     MAIN RENDER
     ------------------------------------------- */
  return (
    <div style={{
      height: '100%',
      padding: `${mob ? 20 : 32}px ${pad}px 0`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      userSelect: dragState ? 'none' : 'auto',
    }}>
      {/* Breadcrumb */}
      {!mob && (
        <div style={{ flexShrink: 0 }}>
          <Breadcrumb items={['Console', 'CTA Pipeline']} />
        </div>
      )}

      {/* --- Header --- */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: mob ? 16 : 20, flexWrap: 'wrap', gap: mob ? 12 : 16, flexShrink: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: mob ? 22 : 28, fontWeight: 900, letterSpacing: '-.03em' }}>CTA Pipeline</h2>
          {!mob && <p style={{ fontSize: 13, color: C.t2, marginTop: 4 }}>Manage and track your lead conversion workflow</p>}
        </div>

        {/* Right toolbar � wraps on smaller screens */}
        <div style={{ display: 'flex', gap: mob ? 8 : 10, alignItems: 'center', flexWrap: 'wrap', width: mob ? '100%' : 'auto' }}>
          {/* Stats pills */}
          <div style={{ display: 'flex', gap: 6, marginRight: mob ? 'auto' : 8 }}>
            <div style={{ padding: mob ? '5px 10px' : '6px 14px', borderRadius: 10, background: '#f1eeec', fontSize: mob ? 11 : 12, fontWeight: 600, color: C.t2, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 800, color: C.t1 }}>{totalLeads}</span> leads
            </div>
            <div style={{ padding: mob ? '5px 10px' : '6px 14px', borderRadius: 10, background: '#f1eeec', fontSize: mob ? 11 : 12, fontWeight: 600, color: C.t2, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 800, color: C.teal }}>${totalValue.toLocaleString()}</span> pipeline
            </div>
          </div>

          {/* Search */}
          <div style={{ position: 'relative', flex: mob ? 1 : 'none', minWidth: mob ? 0 : 'auto' }}>
            <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: C.t3 }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads\u2026"
              style={{ padding: '8px 12px 8px 36px', borderRadius: 10, border: `1px solid ${C.bdr}`, fontSize: 12, outline: 'none', width: mob ? '100%' : 180, boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = C.primary}
              onBlur={e => e.target.style.borderColor = C.bdr} />
          </div>

          {/* View toggle � hidden on mobile (forced to list) */}
          {!mob && (
            <div style={{ display: 'flex', background: '#f1eeec', borderRadius: 12, padding: 3 }}>
              <button onClick={() => setViewMode('board')} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, border: 'none',
                background: viewMode === 'board' ? C.w : 'transparent', fontWeight: viewMode === 'board' ? 700 : 600,
                fontSize: 12, cursor: 'pointer', boxShadow: viewMode === 'board' ? '0 1px 3px rgba(0,0,0,.06)' : 'none',
                color: viewMode === 'board' ? C.t1 : C.t2, transition: 'all .15s',
              }}><Icon name="grid_view" size={16} />Board</button>
              <button onClick={() => setViewMode('list')} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 10, border: 'none',
                background: viewMode === 'list' ? C.w : 'transparent', fontWeight: viewMode === 'list' ? 700 : 600,
                fontSize: 12, cursor: 'pointer', boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,.06)' : 'none',
                color: viewMode === 'list' ? C.t1 : C.t2, transition: 'all .15s',
              }}><Icon name="format_list_bulleted" size={16} />List</button>
            </div>
          )}

          <Btn primary icon="add" onClick={() => openAddModal()} style={mob ? { padding: '8px 14px', fontSize: 12 } : {}}>
            {mob ? 'Add' : 'Add Lead'}
          </Btn>
        </div>
      </div>

      {/* ---------------------------------------
          KANBAN BOARD (desktop + tablet only)
         --------------------------------------- */}
      {effectiveView === 'board' && (
        <div ref={boardRef} style={{
          flex: 1, minHeight: 0,
          overflowX: 'auto', overflowY: 'hidden',
          display: 'flex', gap: isWide ? 18 : 14, alignItems: 'stretch',
          marginLeft: -pad, marginRight: -pad,
          paddingLeft: pad, paddingRight: pad,
          paddingBottom: 0,
          WebkitOverflowScrolling: 'touch',
        }}>
          {filteredColumns.map((col) => {
            const isDropHere = dropTarget?.colId === col.id && dragState;
            const colMinW = isWide ? 290 : 260;
            return (
              <div key={col.id} data-col-id={col.id} style={{
                minWidth: colMinW, maxWidth: colMinW, borderRadius: 14,
                background: isDropHere ? `${col.color}06` : '#f8f6f4',
                borderTop: `4px solid ${col.color}`,
                display: 'flex', flexDirection: 'column',
                flexShrink: 0, overflow: 'hidden',
                transition: 'background .2s',
              }}>
                {/* Column header */}
                <div style={{ padding: '14px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.t2 }}>{col.title}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: `${col.color}18`, color: col.color }}>{col.cards.length}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 2 }}>
                    <button onClick={() => openAddModal(col.id)}
                      style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3, transition: 'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${col.color}14`; e.currentTarget.style.color = col.color; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.t3; }}>
                      <Icon name="add" size={16} />
                    </button>
                    <button style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center', color: C.t3 }}>
                      <Icon name="more_horiz" size={16} />
                    </button>
                  </div>
                </div>

                {/* Cards area */}
                <div style={{ flex: 1, minHeight: 0, padding: '4px 10px 10px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
                  {col.cards.length === 0 && !dragState && (
                    <div style={{ border: `2px dashed ${C.bdr}`, borderRadius: 12, padding: 32, textAlign: 'center', fontSize: 12, color: C.t3, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <Icon name="drag_indicator" size={20} style={{ color: C.t3 }} />
                      Drag leads here
                    </div>
                  )}
                  {col.cards.map((card, idx) => (
                    <React.Fragment key={card.id}>
                      {isDropHere && dropTarget.index === idx && dragState.cardId !== card.id && <DropLine color={col.color} />}
                      {renderCard(card, col.id)}
                    </React.Fragment>
                  ))}
                  {isDropHere && dropTarget.index >= col.cards.length && <DropLine color={col.color} />}
                  {col.cards.length === 0 && dragState && (
                    <div style={{
                      border: `2px dashed ${isDropHere ? col.color : C.bdr}`, borderRadius: 12, padding: 28,
                      textAlign: 'center', fontSize: 12, color: isDropHere ? col.color : C.t3,
                      fontWeight: 600, transition: 'all .2s', background: isDropHere ? `${col.color}08` : 'transparent',
                    }}>Drop here</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------------------------------------
          LIST VIEW
         --------------------------------------- */}
      {effectiveView === 'list' && (
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto', overflowX: mob ? 'auto' : 'hidden',
          marginLeft: -pad, marginRight: -pad, paddingLeft: pad, paddingRight: pad, paddingBottom: 32,
        }}>
          {/* -- Mobile: Card-based list -- */}
          {mob ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredColumns.flatMap(col =>
                col.cards.map(card => {
                  const prColor = PRIORITY_COLORS[card.priority] || C.t3;
                  const stColor = STATUS_COLORS[card.status] || C.t3;
                  return (
                    <div key={card.id} onClick={() => setSelectedCard(card)} style={{
                      background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`,
                      padding: 16, cursor: 'pointer', transition: 'box-shadow .15s',
                    }}>
                      {/* Row 1: icon + name + value */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: card.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <Icon name={card.icon} size={18} style={{ color: card.iconColor }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.name}</div>
                          {card.email && <div style={{ fontSize: 11, color: C.t3, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.email}</div>}
                        </div>
                        {card.value && <div style={{ fontWeight: 700, color: C.teal, fontSize: 13, flexShrink: 0 }}>{card.value}</div>}
                      </div>
                      {/* Row 2: stage + priority + status */}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                          background: `${col.color}12`, color: col.color,
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: col.color }} />
                          {col.title}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 4, background: `${prColor}14`, color: prColor }}>{card.priority}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: stColor, marginLeft: 'auto' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: stColor, boxShadow: `0 0 0 2px ${stColor}30` }} />
                          {card.status === 'active' ? 'Active' : card.status === 'pending' ? 'Pending' : 'Cold'}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              {filteredColumns.every(c => c.cards.length === 0) && (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <Icon name="inbox" size={32} style={{ color: C.t3, display: 'block', margin: '0 auto 8px' }} />
                  <p style={{ fontSize: 13, color: C.t3, fontWeight: 500 }}>No leads match your search.</p>
                </div>
              )}
            </div>
          ) : (
            /* -- Desktop/Tablet: Full table -- */
            <div style={{ background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: isTablet ? 800 : 'auto' }}>
                  <thead>
                    <tr style={{ background: '#faf9f8', borderBottom: `1px solid ${C.bdr}` }}>
                      {(isTablet
                        ? ['Lead', 'Value', 'Stage', 'Priority', 'Status', '']
                        : ['Lead', 'Value', 'Stage', 'Priority', 'Status', 'Contact', 'Tags', '']
                      ).map(h => (
                        <th key={h} style={{
                          padding: isTablet ? '10px 14px' : '11px 20px', textAlign: h === '' ? 'right' : 'left',
                          fontSize: 10, fontWeight: 700, color: C.t3, textTransform: 'uppercase',
                          letterSpacing: '.06em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredColumns.flatMap(col =>
                      col.cards.map(card => {
                        const prColor = PRIORITY_COLORS[card.priority] || C.t3;
                        const stColor = STATUS_COLORS[card.status] || C.t3;
                        const stLabel = card.status === 'active' ? 'Active' : card.status === 'pending' ? 'Pending' : 'Cold';
                        const cellPad = isTablet ? '12px 14px' : '14px 20px';
                        return (
                          <tr key={card.id}
                            style={{ borderBottom: `1px solid ${C.bdr}`, cursor: 'pointer', transition: 'background .1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#faf8f7'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            onClick={() => setSelectedCard(card)}>
                            {/* Lead */}
                            <td style={{ padding: cellPad }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 34, height: 34, borderRadius: 10, background: card.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                                  <Icon name={card.icon} size={18} style={{ color: card.iconColor }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: 13 }}>{card.name}</div>
                                  {card.email && <div style={{ fontSize: 11, color: C.t3, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isTablet ? 160 : 'none' }}>{card.email}</div>}
                                </div>
                              </div>
                            </td>
                            {/* Value */}
                            <td style={{ padding: cellPad, fontWeight: 700, color: C.teal, whiteSpace: 'nowrap' }}>{card.value || '\u2014'}</td>
                            {/* Stage */}
                            <td style={{ padding: cellPad }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: `${col.color}12`, color: col.color }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: col.color }} />
                                {col.title}
                              </span>
                            </td>
                            {/* Priority */}
                            <td style={{ padding: cellPad }}>
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: `${prColor}14`, color: prColor }}>{card.priority}</span>
                            </td>
                            {/* Status */}
                            <td style={{ padding: cellPad }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: stColor }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: stColor, boxShadow: `0 0 0 2px ${stColor}30` }} />
                                {stLabel}
                              </span>
                            </td>
                            {/* Contact � hidden on tablet */}
                            {!isTablet && (
                              <td style={{ padding: cellPad }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <Icon name="person" size={14} style={{ color: C.t3 }} />
                                  <span style={{ fontSize: 12, color: C.t2 }}>{card.contact || '\u2014'}</span>
                                </div>
                              </td>
                            )}
                            {/* Tags � hidden on tablet */}
                            {!isTablet && (
                              <td style={{ padding: cellPad }}>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {card.tags.map(t => (
                                    <span key={t} style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: '#f1eeec', color: C.t2, textTransform: 'uppercase' }}>{t}</span>
                                  ))}
                                </div>
                              </td>
                            )}
                            {/* Actions */}
                            <td style={{ padding: cellPad, textAlign: 'right' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                                <button onClick={(e) => { e.stopPropagation(); openEditModal(card); }}
                                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, padding: 4, borderRadius: 6, display: 'grid', placeItems: 'center' }}
                                  onMouseEnter={e => e.currentTarget.style.color = C.primary}
                                  onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                                  <Icon name="edit" size={16} />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setSelectedCard(card); }}
                                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, padding: 4, borderRadius: 6, display: 'grid', placeItems: 'center' }}
                                  onMouseEnter={e => e.currentTarget.style.color = C.primary}
                                  onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                                  <Icon name="visibility" size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                    {filteredColumns.every(c => c.cards.length === 0) && (
                      <tr>
                        <td colSpan={isTablet ? 6 : 8} style={{ padding: 48, textAlign: 'center' }}>
                          <Icon name="inbox" size={32} style={{ color: C.t3, marginBottom: 8 }} />
                          <p style={{ fontSize: 13, color: C.t3, fontWeight: 500 }}>No leads match your search.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Drag ghost (fixed, follows cursor � desktop only) --- */}
      {dragState && draggedCard && (
        <div style={{ position: 'fixed', top: dragState.ghostTop, left: dragState.ghostLeft, width: dragState.ghostW, zIndex: 999, pointerEvents: 'none' }}>
          {renderCard(draggedCard, '', true)}
        </div>
      )}

      {/* ---------------------------------------
          CARD DETAIL MODAL
         --------------------------------------- */}
      {selectedCard && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,.2)', display: 'grid', placeItems: 'center', padding: mob ? 16 : 24 }} onClick={() => setSelectedCard(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: typeof detailW === 'string' ? detailW : detailW,
            ...(typeof detailW === 'number' ? { width: detailW } : { width: detailW }),
            background: C.w, borderRadius: 16, border: `1px solid ${C.bdr}`,
            boxShadow: '0 20px 60px rgba(0,0,0,.12)', overflow: 'hidden',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ height: 5, background: selectedCard.iconColor, flexShrink: 0 }} />
            <div style={{ padding: mob ? 20 : 24, overflowY: 'auto', flex: 1 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: selectedCard.iconBg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Icon name={selectedCard.icon} size={22} style={{ color: selectedCard.iconColor }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: mob ? 16 : 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedCard.name}</h3>
                    {selectedCard.value && <span style={{ fontSize: 14, fontWeight: 700, color: C.teal }}>{selectedCard.value}</span>}
                  </div>
                </div>
                <button onClick={() => setSelectedCard(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Icon name="close" size={20} />
                </button>
              </div>

              {/* Tags + priority */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {selectedCard.tags.map(t => (
                  <span key={t} style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#f1eeec', color: C.t2, textTransform: 'uppercase' }}>{t}</span>
                ))}
                {selectedCard.priority && <Pill bg={`${PRIORITY_COLORS[selectedCard.priority]}14`} color={PRIORITY_COLORS[selectedCard.priority]}>{selectedCard.priority}</Pill>}
              </div>

              {/* Info rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: C.t2 }}>
                {selectedCard.contact && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="person" size={18} style={{ color: C.t3 }} /><span>{selectedCard.contact}</span></div>}
                {selectedCard.email && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="mail" size={18} style={{ color: C.t3 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedCard.email}</span></div>}
                {selectedCard.createdAt && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="schedule" size={18} style={{ color: C.t3 }} /><span>Created {selectedCard.createdAt}</span></div>}
                {selectedCard.notes && <div style={{ display: 'flex', gap: 10, marginTop: 4 }}><Icon name="description" size={18} style={{ color: C.t3, flexShrink: 0, marginTop: 2 }} /><span style={{ lineHeight: 1.6 }}>{selectedCard.notes}</span></div>}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: `1px solid ${C.bdr}` }}>
                <Btn icon="edit" style={{ flex: 1, justifyContent: 'center' }} onClick={() => {
                  const cid = columns.find(c => c.cards.some(cd => cd.id === selectedCard.id))?.id;
                  openEditModal(selectedCard, cid);
                }}>Edit</Btn>
                <button onClick={() => deleteCard(selectedCard.id)} style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '9px 18px', borderRadius: 12, border: `1px solid ${C.red}30`,
                  background: `${C.red}08`, color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>
                  <Icon name="delete" size={16} />Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------
          ADD / EDIT MODAL
         --------------------------------------- */}
      {showAddModal && formData && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,.25)', display: 'grid', placeItems: 'center', padding: mob ? 12 : 24 }}
          onClick={() => { setShowAddModal(false); setEditingId(null); }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: typeof formW === 'number' ? formW : undefined,
            ...(typeof formW === 'string' ? { width: formW } : {}),
            maxHeight: mob ? '95vh' : '90vh',
            background: C.w, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.15)',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Modal header */}
            <div style={{ padding: mob ? '16px 20px' : '20px 24px', borderBottom: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ fontSize: mob ? 16 : 18, fontWeight: 700 }}>{editingId ? 'Edit Lead' : 'New Lead'}</h3>
              <button onClick={() => { setShowAddModal(false); setEditingId(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.t3, display: 'grid', placeItems: 'center' }}>
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* Modal body (scrollable) */}
            <div style={{ padding: mob ? '16px 20px' : 24, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1 }}>
              {/* Stage selector */}
              <div>
                <label style={labelStyle}>Stage</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {columns.map(col => (
                    <button key={col.id} onClick={() => setAddToColumn(col.id)} style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: addToColumn === col.id ? `2px solid ${col.color}` : `1px solid ${C.bdr}`,
                      background: addToColumn === col.id ? `${col.color}12` : C.w,
                      color: addToColumn === col.id ? col.color : C.t2,
                    }}>{col.title}</button>
                  ))}
                </div>
              </div>

              {/* Company name */}
              <div>
                <label style={labelStyle}>Company Name</label>
                <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Acme Inc." autoFocus style={inputStyle} {...focusHandlers} />
              </div>

              {/* Contact + email row � stack on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Contact Person</label>
                  <input value={formData.contact} onChange={e => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="Sarah Chen" style={inputStyle} {...focusHandlers} />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="sarah@acme.io" style={inputStyle} {...focusHandlers} />
                </div>
              </div>

              {/* Value + tags � stack on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Deal Value</label>
                  <input value={formData.value} onChange={e => setFormData({ ...formData, value: e.target.value })}
                    placeholder="$24,000" style={inputStyle} {...focusHandlers} />
                </div>
                <div>
                  <label style={labelStyle}>Tags (comma separated)</label>
                  <input value={formData.tags} onChange={e => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="AI, Enterprise" style={inputStyle} {...focusHandlers} />
                </div>
              </div>

              {/* Priority + Status � stack on mobile */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Priority</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['High', 'Medium', 'Low'].map(p => (
                      <button key={p} onClick={() => setFormData({ ...formData, priority: p })} style={{
                        flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        border: formData.priority === p ? `2px solid ${PRIORITY_COLORS[p]}` : `1px solid ${C.bdr}`,
                        background: formData.priority === p ? `${PRIORITY_COLORS[p]}12` : C.w,
                        color: formData.priority === p ? PRIORITY_COLORS[p] : C.t2,
                      }}>{p}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['active', 'pending', 'cold'].map(s => (
                      <button key={s} onClick={() => setFormData({ ...formData, status: s })} style={{
                        flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                        border: formData.status === s ? `2px solid ${STATUS_COLORS[s]}` : `1px solid ${C.bdr}`,
                        background: formData.status === s ? `${STATUS_COLORS[s]}12` : C.w,
                        color: formData.status === s ? STATUS_COLORS[s] : C.t2,
                      }}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Icon picker */}
              <div>
                <label style={labelStyle}>Icon</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {ICON_OPTIONS.map(opt => (
                    <button key={opt.icon} onClick={() => setFormData({ ...formData, icon: opt.icon, iconBg: opt.bg, iconColor: opt.color })} style={{
                      width: 38, height: 38, borderRadius: 10,
                      border: formData.icon === opt.icon ? `2px solid ${opt.color}` : `1px solid ${C.bdr}`,
                      background: opt.bg, cursor: 'pointer', display: 'grid', placeItems: 'center',
                      transform: formData.icon === opt.icon ? 'scale(1.1)' : 'scale(1)', transition: 'transform .15s',
                    }}>
                      <Icon name={opt.icon} size={18} style={{ color: opt.color }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any details about this lead\u2026" rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }} {...focusHandlers} />
              </div>
            </div>

            {/* Modal footer */}
            <div style={{ padding: mob ? '14px 20px' : '16px 24px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
              <Btn onClick={() => { setShowAddModal(false); setEditingId(null); }}>Cancel</Btn>
              <Btn primary icon="check" onClick={saveCard}>{editingId ? 'Save Changes' : 'Add Lead'}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
